import type { Gender, Relation } from "../../types";
import { SELECTABLE_RELATIONS } from "../../types";
import { MAX_WEIGHT_LBS, MIN_REALISTIC_WEIGHT_LBS } from "../../calc";
import { FieldRow } from "../FieldRow";
import { Alert } from "../Alert";
import { ButtonGroupInput, DateInput, MaskedInput, NumberInput, SelectInput, TextArea, TextInput, ToggleInput, type SelectOption } from "../fields";
import type { FieldErrors } from "../../validation/rules";

export interface MemberFormValues {
  firstName: string;
  lastName: string;
  relation: Relation | null;
  subscriberMemberId: string;
  gender: Gender | "" | null;
  dateOfBirth: string;
  heightFeet: number | null;
  heightInches: number | null;
  weightLbs: number | null;
  postalCode: string;
  ssn: string;
  notes: string;
  isExcluded: boolean;
}

export type MemberFormField = keyof MemberFormValues;

const RELATION_OPTIONS = SELECTABLE_RELATIONS.map((value) => ({ value, label: value }));
/** A migrated or Skynet-published Dependent Adult keeps that relation unless the underwriter changes it; nobody else can become one. */
const LEGACY_DEPENDENT_ADULT_OPTIONS = [...RELATION_OPTIONS, { value: "Dependent Adult" as const, label: "Dependent Adult" }];
const NO_SUBSCRIBER_MESSAGE = "No Employee Available to Select";
const NO_SUBSCRIBER_OPTIONS: readonly SelectOption[] = [{ value: "", label: NO_SUBSCRIBER_MESSAGE }];
const SEX_OPTIONS = [
  { value: "M", label: "M" },
  { value: "F", label: "F" },
  { value: "", label: "Unknown" },
] as const;

interface MemberFormFieldsProps {
  values: MemberFormValues;
  errors: FieldErrors<MemberFormField>;
  onChange: (values: MemberFormValues) => void;
  subscriberOptions: readonly SelectOption[];
  needsSubscriber: boolean;
  /** False when the group has no other employee: the select still shows, disabled, so the gap is visible rather than silent. */
  hasSubscriberCandidates: boolean;
  storedSsnMasked: string | null;
  previewBuild: number | null;
  heightWarning: boolean;
  weightWarning: boolean;
  children?: never;
}

/** The Add / Edit Member field set, including the live build hint and realism warnings. */
export function MemberFormFields({ values, errors, onChange, subscriberOptions, needsSubscriber, hasSubscriberCandidates, storedSsnMasked, previewBuild, heightWarning, weightWarning }: MemberFormFieldsProps) {
  const set = <K extends MemberFormField>(key: K, value: MemberFormValues[K]) => onChange({ ...values, [key]: value });

  return (
    <div className="space-y-4">
      <FieldRow>
        <TextInput id="member-first" label="First Name" value={values.firstName} onChange={(value) => set("firstName", value)} error={errors.firstName} required />
        <TextInput id="member-last" label="Last Name" value={values.lastName} onChange={(value) => set("lastName", value)} error={errors.lastName} required />
      </FieldRow>
      <ButtonGroupInput id="member-relation" label="Relation" value={values.relation} onChange={(value) => set("relation", value)} options={values.relation === "Dependent Adult" ? LEGACY_DEPENDENT_ADULT_OPTIONS : RELATION_OPTIONS} error={errors.relation} required />
      {needsSubscriber && (
        <SelectInput
          id="member-subscriber"
          label="Subscriber"
          value={values.subscriberMemberId}
          onChange={(value) => set("subscriberMemberId", value)}
          options={hasSubscriberCandidates ? subscriberOptions : NO_SUBSCRIBER_OPTIONS}
          placeholder={hasSubscriberCandidates ? undefined : NO_SUBSCRIBER_MESSAGE}
          disabled={!hasSubscriberCandidates}
          hint={hasSubscriberCandidates ? undefined : "Add an employee to the group first; a dependent needs a subscriber."}
          error={errors.subscriberMemberId}
          required
        />
      )}
      {values.relation === "Employee" && <p className="text-xs text-gray-500">Employees are their own subscriber.</p>}
      <FieldRow columns={3}>
        <ButtonGroupInput id="member-sex" label="Sex" value={values.gender === null ? null : values.gender} onChange={(value) => set("gender", value)} options={SEX_OPTIONS} />
        <DateInput id="member-dob" label="DOB" value={values.dateOfBirth} onChange={(value) => set("dateOfBirth", value)} error={errors.dateOfBirth} />
        <TextInput id="member-zip" label="ZIP" value={values.postalCode} onChange={(value) => set("postalCode", value)} error={errors.postalCode} hint="Optional; helps Sales line this person up with the quote census." />
      </FieldRow>
      <FieldRow columns={3}>
        <NumberInput id="member-ft" label="Height (ft)" value={values.heightFeet} onChange={(value) => set("heightFeet", value)} error={errors.heightFeet} min={0} max={9} />
        <NumberInput id="member-in" label="Height (in)" value={values.heightInches} onChange={(value) => set("heightInches", value)} error={errors.heightInches} min={0} max={11} />
        <NumberInput id="member-weight" label="Weight (lbs)" value={values.weightLbs} onChange={(value) => set("weightLbs", value)} error={errors.weightLbs} min={0} max={MAX_WEIGHT_LBS} />
      </FieldRow>
      {previewBuild !== null && (
        <p className="text-sm text-gray-600" aria-live="polite">
          Build points: <strong className="tabular-nums text-brand-navy">{previewBuild}</strong>
        </p>
      )}
      {(heightWarning || weightWarning) && <Alert type="warning" message={`${heightWarning ? "Height is outside 1'0\" to 7'11\"." : ""} ${weightWarning ? `Weight is under ${MIN_REALISTIC_WEIGHT_LBS} lbs.` : ""} Check the entry; you can still save.`.trim()} />}
      <MaskedInput id="member-ssn" label="SSN" mask="ssn" value={values.ssn} onChange={(digits) => set("ssn", digits)} storedMasked={storedSsnMasked} error={errors.ssn} hint="Stored encrypted and never shown in grids." />
      <TextArea id="member-notes" label="Notes" value={values.notes} onChange={(value) => set("notes", value)} />
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <ToggleInput id="member-excluded" label="Excluded from the group" checked={values.isExcluded} onChange={(value) => set("isExcluded", value)} />
        <p className="mt-1 text-xs text-gray-500">Excluded members keep their ratings but drop out of every count, total, and RMM. Excluding the last real member of a Spock cohort excludes that cohort too.</p>
      </div>
    </div>
  );
}
